// Dependencies
import React, {Component} from "react";
import {connect} from "react-redux";
import {withRouter, RouteComponentProps, Link} from "react-router-dom";

// Components
import {Components} from "@webtech/webtech-ui-react/build/webtech-ui";
import {SubsidiaryUtils} from "../../../../utils/Subsidiary/SubsidiaryUtils";
import Paginator from "../../../../utils/Paginator/Paginator.component";

// Store
import {ActionCreator} from "../../../../actions/actions";
import {presetsStoreSelectors} from "../../store/store";
import {PresetsActionCreators} from "../../actions/actionCreators";
import {Subsidiary} from "../../../halls/types/subsidiaries";

// Utils
import {checkPermission} from "../../../../utils/Authorization/authorization";
import {formatDateText} from "../../../../utils/utils";

// Styles
import "./PresetsList.component.scss";
import {Dialog} from "../../../../utils/Dialog/Dialog.component";

// Constants
const PRESETS_PER_PAGE = 21;
const INITIAL_PAGE = 0;

const JACKPOTS_KEYS = {
    0: "Levels",
    1: "Multipots",
    2: "EasyLink",
    3: "SAP",
    4: "Fractionated",
    7: "Reel Strike",
    8: "Jumbo Link",
    9: "LuckyToken"
};

const JACKPOTS_PRIZE_LEVEL = {
    child: "Prog. Group Prizes",
    parent: "Shared Prizes",
    null: "All Prizes",
    single: "Single Prize"
};

const SAP_JACKPOTS_PRIZE_LEVEL = {
    child: "Minor",
    parent: "Grand"
};

const prizeTypesGeneral = [
    {
        label: "Shared Prize",
        value: "parent"
    },
    {
        label: "Prog. Group Prizes",
        value: "child"
    },
    {
        label: "Single Prize",
        value: "single"
    },
    {
        label: "All Prizes",
        value: null
    }
];

const sapPrizeTypes = [
    {
        label: "Grand",
        value: "parent"
    },
    {
        label: "Minor",
        value: "child"
    }
];

export interface PresetsListProps extends RouteComponentProps {
    getSubsidiaries: ActionCreator;
    getPresets: ActionCreator;
    activatePreset: ActionCreator;
    deactivatePreset: ActionCreator;
    subsidiariesLoading: boolean;
    subsidiariesList: Subsidiary[];
    presetsLoading: boolean;
    presetsList: {
        presets: any[];
        timestamp: string;
        count: number;
    };
    certifiedPreset: ActionCreator;
}

export interface PresetsListState {
    searchText: string;
    subsidiaryFilterValue: string[];
    typeFilterValue: string[];
    prizeTypeFilterValue: string[];
    statusFilterValue: string;
    certifiedFilterValue: string;
    currentPage: number;
    showConfirmPreset: boolean;
    showConfirmPresetId: number;
    showConfirmPresetValue: boolean;
}

class PresetsList extends Component<PresetsListProps, PresetsListState> {
    subsidiaryFilter: React.RefObject<Components.SelectMenu>;
    typeFilter: React.RefObject<Components.SelectMenu>;
    prizeTypeFilter: React.RefObject<Components.SelectMenu>;
    canEdit: boolean;
    clearFilters: boolean;

    constructor(props) {
        super(props);

        this.props.getSubsidiaries();

        this.subsidiaryFilter = React.createRef();
        this.typeFilter = React.createRef();
        this.prizeTypeFilter = React.createRef();

        this.state = {
            searchText: "",
            subsidiaryFilterValue: [],
            typeFilterValue: [],
            prizeTypeFilterValue: [],
            statusFilterValue: "1",
            certifiedFilterValue: "",
            currentPage: INITIAL_PAGE,
            showConfirmPreset: false,
            showConfirmPresetId: 0,
            showConfirmPresetValue: false
        };

        this.getPresets()

        this.canEdit = checkPermission("toggle_prog_presets");
        this.clearFilters = false
    }

    getPresets() {
        this.props.getPresets({
            page: this.state.currentPage,
            top: PRESETS_PER_PAGE,
            search: this.state.searchText,
            subsidiaries: this.state.subsidiaryFilterValue,
            types: this.state.typeFilterValue,
            prizeType: this.state.prizeTypeFilterValue,
            status: this.state.statusFilterValue,
            certified: this.state.certifiedFilterValue
        })
    }

    applyFilters = (value, field) => {
        if (!this.clearFilters) {
            let updatedState = {...this.state};
            updatedState[field] = value
            this.setState({
                ...updatedState,
                currentPage: INITIAL_PAGE
            }, this.getPresets)
        }
    }

    pageChange = newPage => {
        this.setState({currentPage: newPage - 1}, this.getPresets);
    };

    onClearFilters = () => {
        this.clearFilters = true

        this.subsidiaryFilter.current.selectItem([]);
        this.subsidiaryFilter.current.setDisplayString("Choose Subsidiary");

        this.typeFilter.current.selectItem([]);
        this.typeFilter.current.setDisplayString("Choose Types");

        this.prizeTypeFilter.current.selectItem([]);
        this.prizeTypeFilter.current.setDisplayString("Choose Prize Types");

        this.setState({
            searchText: "",
            subsidiaryFilterValue: [],
            typeFilterValue: [],
            prizeTypeFilterValue: [],
            statusFilterValue: "1",
            certifiedFilterValue: ""
        }, this.getPresets);
        this.clearFilters = false
    };

    exportData = (data) => {
        const link = document.createElement("a");
        link.href = `data:text/json;chatset=utf-8,${encodeURIComponent(
            JSON.stringify(data)
        )}`;
        link.download = data.name + ".json";
        link.click();
    };

    renderPresets = () => {
        if (this.props.presetsList.presets && this.props.presetsList.presets.length === 0) {
            return (
                <div className="preset-list-no-preset">
                    No Progressives Presets records to display. Please select different
                    values to try again.
                </div>
            );
        }
        return (
            this.props.presetsList.presets &&
            this.props.presetsList.presets.map((preset, index) => {
                const sub = this.props.subsidiariesList.find(
                    subsidiary => subsidiary.id == preset.subsidiary
                );
                return (
                    <div className="preset-card" key={index}>
                        <div className="preset-card-body">
                            <div className="preset-card-btns">
                                {preset.active && checkPermission("certified_prog_presets") && (
                                    <span
                                        className="btn tooltip-icon"
                                        onClick={() => {
                                            this.setState({
                                                showConfirmPreset: true,
                                                showConfirmPresetId: preset.id,
                                                showConfirmPresetValue: preset.certified
                                            })
                                        }}
                                    >
                                        <span
                                            className="tooltip-text">{preset.certified ? "Uncertify " : "Certify"}</span>
                                        <i className={`icon-file-text ${preset.certified && "green"}`}/>
                                    </span>
                                )}
                                {checkPermission("add_prog_presets") && (
                                    <Link
                                        className="btn tooltip-icon"
                                        to={`/admin/progressives-presets/notes/${preset.id}/`}
                                    >
                                        <span className="tooltip-text">Edit notes</span>
                                        <i className="icon-pencil-alt-light"></i>
                                    </Link>
                                )}
                                {checkPermission("add_prog_presets") && (
                                    <Link
                                        className="btn tooltip-icon"
                                        to={`/admin/progressives-presets/edit/${preset.id}/`}
                                    >
                                        <span className="tooltip-text">Clone</span>
                                        <i className="icon-clone-light"/>
                                    </Link>
                                )}
                                {checkPermission("add_prog_presets") && (
                                    <div>
                                        <button
                                            className="btn tooltip-icon"
                                            onClick={() => this.exportData(preset)}
                                        >
                                            <span className="tooltip-text">Download</span>
                                            <i className="icon-arrow-to-bottom-light"/>

                                        </button>
                                    </div>
                                )}
                            </div>

                            <h2>{preset.name}</h2>
                            <p className="icon_info"><i
                                className={`icon-file-text ${preset.certified && "green"}`}/>{preset.certified ? "Certified" : "Not Certified"}
                            </p>
                            <label>Id: {preset.id} </label>
                            <label>Type Applicable</label>
                            <p>{JACKPOTS_KEYS[preset.type]}</p>
                            <label>Prize Type Applicable</label>
                            <p>
                                {preset.type !== "3"
                                    ? JACKPOTS_PRIZE_LEVEL[preset.stype]
                                    : SAP_JACKPOTS_PRIZE_LEVEL[preset.stype]}
                            </p>
                            <label>Subsidiary</label>
                            {sub && (
                                <div className="subsidiary">
                                    {SubsidiaryUtils.subsidiaryIcon(sub)}
                                    {sub.description}
                                </div>
                            )}
                            {sub == undefined && "-"}
                            <label>Creation Date</label>
                            <p>
                                {formatDateText(
                                    preset.creation_date, "short", true, true, true
                                )}
                            </p>
                            <label>Notes</label>
                            <div className="text-overflow">
                                {preset.notes && preset.notes != "" && preset != 'null' ?
                                    (<span>{preset.notes}</span>)
                                    : (<span>-</span>)
                                }
                            </div>
                        </div>
                        <div className="preset-card-footer">
                            <a
                                className="preset-card-link"
                                href={`/admin/progressives-presets/detail/${preset.id}/`}
                            >
                                Details
                            </a>

                            {this.canEdit && (
                                <div className="preset-card-status">
                                    <span>
                                        {preset.active ? "Active" : "Inactive"}
                                    </span>
                                    <Components.Switch
                                        onClick={() =>
                                            preset.active
                                                ? this.props.deactivatePreset(
                                                    preset.id
                                                )
                                                : this.props.activatePreset(
                                                    preset.id
                                                )
                                        }
                                        on={preset.active}
                                        enabled={
                                            !(preset.default == true)
                                        }
                                    />
                                </div>
                            )}

                            {/*this.canEdit &&
                                !(
                                    preset.subsidiary == undefined ||
                                    preset.subsidiary == ""
                                ) && (
                                    <a
                                        className="icon-edit"
                                        href={`/admin/progressives-presets/edit/${preset.id}/`}
                                    >
                                        <i className="icon-pencil-alt-light"></i>
                                    </a>
                                )*/}
                        </div>
                    </div>
                );
            })
        );
    };

    certifiedPreset = () => {
        this.props.certifiedPreset(this.state.showConfirmPresetId, !this.state.showConfirmPresetValue);
        this.setState({
            showConfirmPreset: false,
            showConfirmPresetId: 0,
            showConfirmPresetValue: false
        })
    };

    render() {
        const isLoading =
            this.props.subsidiariesLoading || this.props.presetsLoading;
        const presetStatus = [
            {
                value: "",
                label: "All"
            },
            {
                value: 1,
                label: "Active"
            },
            {
                value: 0,
                label: "Inactive"
            }
        ];
        const presetCertified = [
            {
                value: "",
                label: "All"
            },
            {
                value: 1,
                label: "Certified"
            },
            {
                value: 0,
                label: "Not Certified"
            }
        ];

        return (
            <div id="PresetsList" className="content">
                <div className="container">
                    <div className="row">
                        <div className="col-6">
                            <h2>Progressives Presets</h2>
                        </div>
                        <div className="col-6 center-vertical buttons-col">
                            {checkPermission("add_prog_presets") && (
                                <Link
                                    to={`/admin/progressives-presets/new`}
                                    className="new-preset red-btn"
                                >
                                    <span>New Progressive Preset</span>
                                </Link>
                            )}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="presets-filters">
                                <div id="PresetsPageFiltersBox">
                                    <div className="row">
                                        {this.props.subsidiariesLoading && (
                                            <div className="presets-list-loading">
                                                <Components.LoadingRipple
                                                    width={"75px"}
                                                    height={"75px"}
                                                />
                                            </div>
                                        )}
                                        {!this.props.subsidiariesLoading && (
                                            <div className="adjustable-filters">
                                                <div className="preset-filter">
                                                    <label className="label">
                                                        Search
                                                    </label>
                                                    <input
                                                        className="input"
                                                        onChange={event =>
                                                            this.applyFilters(event.target.value, 'searchText')}
                                                        value={
                                                            this.state
                                                                .searchText
                                                        }
                                                        placeholder="Text"
                                                    />
                                                </div>

                                                <div className="preset-filter">
                                                    <label className="label">
                                                        Subsidiaries
                                                    </label>
                                                    <Components.SelectMenu
                                                        key={`${this.state.subsidiaryFilterValue}`}
                                                        ref={this.subsidiaryFilter}
                                                        noValueLabel="Choose Subsidiary"
                                                        multipleSelection
                                                        items={
                                                            this.props
                                                                .subsidiariesList &&
                                                            this.props
                                                                .subsidiariesList
                                                                .length > 0
                                                                ? SubsidiaryUtils.subsidiarySelectItems(
                                                                    this.props
                                                                        .subsidiariesList
                                                                )
                                                                : []
                                                        }
                                                        onChange={event => this.applyFilters(event, 'subsidiaryFilterValue')}
                                                        value={this.state.subsidiaryFilterValue}
                                                    />
                                                </div>

                                                <div className="preset-filter">
                                                    <label className="label">
                                                        Type
                                                    </label>
                                                    <Components.SelectMenu
                                                        ref={this.typeFilter}
                                                        noValueLabel="Choose Types"
                                                        items={[
                                                            {
                                                                label: "Levels",
                                                                value: "0"
                                                            },
                                                            {
                                                                label: "Multipots",
                                                                value: "1"
                                                            },
                                                            {
                                                                label: "EasyLink",
                                                                value: "2"
                                                            },
                                                            {
                                                                label: "SAP",
                                                                value: "3"
                                                            },
                                                            {
                                                                label: "Fractionated",
                                                                value: "4"
                                                            },
                                                            {
                                                                label: "Reel Strike",
                                                                value: "7"
                                                            },
                                                            {
                                                                label: "Jumbo Link",
                                                                value: "8"
                                                            },
                                                            {
                                                                label: "LuckyToken",
                                                                value: "9"
                                                            }
                                                        ]}
                                                        onChange={event => this.applyFilters(event, 'typeFilterValue')}
                                                        value={
                                                            this.state
                                                                .typeFilterValue
                                                                ? this.state
                                                                    .typeFilterValue[0]
                                                                : ""
                                                        }
                                                    />
                                                </div>

                                                <div className="preset-filter">
                                                    <label className="label">
                                                        Prize Type
                                                    </label>
                                                    <Components.SelectMenu
                                                        ref={
                                                            this.prizeTypeFilter
                                                        }
                                                        noValueLabel="Choose Prize Types"
                                                        items={
                                                            !this.state
                                                                .typeFilterValue ||
                                                            this.state
                                                                .typeFilterValue[0] !==
                                                            "3"
                                                                ? prizeTypesGeneral
                                                                : sapPrizeTypes
                                                        }
                                                        onChange={event => this.applyFilters(event, 'prizeTypeFilterValue')}
                                                        value={
                                                            this.state
                                                                .prizeTypeFilterValue
                                                                ? this.state
                                                                    .prizeTypeFilterValue[0]
                                                                : ""
                                                        }
                                                    />
                                                </div>
                                                <div className="adjustable-filters">
                                                    <div className="preset-filter">
                                                        <label className="label">
                                                            Status
                                                        </label>
                                                        <Components.CheckboxGroup
                                                            key={this.state.statusFilterValue}
                                                            items={presetStatus}
                                                            selected={this.state.statusFilterValue}
                                                            onChange={event => this.applyFilters(event, 'statusFilterValue')}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="preset-filter">
                                                    <label className="label">
                                                        Certified
                                                    </label>
                                                    <Components.CheckboxGroup
                                                        key={this.state.certifiedFilterValue}
                                                        items={presetCertified}
                                                        selected={this.state.certifiedFilterValue}
                                                        onChange={event => this.applyFilters(event, 'certifiedFilterValue')}
                                                    />
                                                </div>

                                                <div>
                                                    <button
                                                        className="clear-filters-btn"
                                                        onClick={
                                                            this.onClearFilters
                                                        }
                                                    >
                                                        Clear All Filters
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="presets-list">
                        {isLoading ? (
                            <div className="presets-list-loading">
                                <Components.LoadingRipple
                                    width={"100px"}
                                    height={"100px"}
                                />
                            </div>
                        ) : (
                            <div className="presets row">
                                {this.renderPresets()}
                            </div>
                        )}
                    </div>
                </div>
                {!isLoading && (
                    <div className="row presets-pagination container">
                        <div className="col-6 center-vertical last-update">
                            <p>{`Last update: ${this.props.presetsList.timestamp}`}</p>
                        </div>

                        <div className="col-6 center-vertical paginator">
                            {this.props.presetsList.presets &&
                                this.props.presetsList.count > 0 && (
                                    <Paginator
                                        currentPage={this.state.currentPage + 1}
                                        totalPages={Math.ceil(
                                            this.props.presetsList.count /
                                            PRESETS_PER_PAGE
                                        )}
                                        onPageChange={this.pageChange}
                                    />
                                )}
                        </div>
                    </div>
                )}
                <Dialog
                    open={this.state.showConfirmPreset}
                    onConfirm={this.certifiedPreset}
                    onClose={() => {
                        this.setState({
                            showConfirmPreset: false,
                            showConfirmPresetId: 0,
                            showConfirmPresetValue: false
                        })
                    }}
                >
                    Are you sure you want to change the certified status for this preset?
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    subsidiariesList: presetsStoreSelectors.subsidiariesList(state),
    subsidiariesLoading: presetsStoreSelectors.subsidiariesLoading(state),
    presetsLoading: presetsStoreSelectors.presetsLoading(state),
    presetsList: presetsStoreSelectors.presetsList(state)
});

const mapDispatchToProps = {
    getSubsidiaries: PresetsActionCreators.getSubsidiaries,
    getPresets: PresetsActionCreators.getPresets,
    deactivatePreset: PresetsActionCreators.deactivatePreset,
    activatePreset: PresetsActionCreators.activatePreset,
    certifiedPreset: PresetsActionCreators.certifiedPreset
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withRouter(PresetsList));